import { getAddTime, getDateObject } from '@/js/utils/date'

const ARCHIVE_TIME_UPDATE_INTERVAL = 300
const AUTO_PLAY_MAX_COUNT = 60

export type StartDate = {
  date: Date
  time: string
}

type NextArchive = 'minute' | 'hour' | null

type PlaybackTimeUpdate = {
  shouldContinue: boolean
  nextArchive: NextArchive
  autoPlayCount: number
}

type LoadedArchivePlayback = {
  shouldPlay: boolean
  autoPlayCount: number
}

type PlaybackTimeUpdateTimeoutId = ReturnType<typeof setTimeout> | null

export function schedulePlaybackTimeUpdate(update: () => void): ReturnType<typeof setTimeout> {
  return setTimeout(update, ARCHIVE_TIME_UPDATE_INTERVAL)
}

export function cancelPlaybackTimeUpdate(timeoutId: PlaybackTimeUpdateTimeoutId): void {
  if (timeoutId !== null) {
    clearTimeout(timeoutId)
  }
}

export function resolvePlaybackTimeUpdate(
  currentTime: number,
  duration: number,
  isPaused: boolean,
  displayDate: string,
  autoPlayCount: number
): PlaybackTimeUpdate {
  if (currentTime < duration) {
    return {
      shouldContinue: !isPaused,
      nextArchive: null,
      autoPlayCount,
    }
  }

  if (currentTime >= duration) {
    const nextArchive = getDateObject(displayDate).getMinutes() === 59 ? 'hour' : 'minute'
    return {
      shouldContinue: false,
      nextArchive,
      autoPlayCount: nextArchive === 'hour' ? autoPlayCount : autoPlayCount + 1,
    }
  }

  return {
    shouldContinue: false,
    nextArchive: null,
    autoPlayCount,
  }
}

export function getNextMinuteStartDate(displayDate: string): StartDate {
  const nextTime = getAddTime(displayDate, 1, 'minutes')
  return {
    date: nextTime,
    time: `${nextTime.getHours()}:${nextTime.getMinutes()}`,
  }
}

export function getNextHourStartDate(displayDate: string): StartDate {
  const nextTime = getAddTime(displayDate, 1, 'hours')
  return {
    date: nextTime,
    time: `${nextTime.getHours()}:00`,
  }
}

export function resolveLoadedArchivePlayback(autoPlayCount: number): LoadedArchivePlayback {
  if (AUTO_PLAY_MAX_COUNT > autoPlayCount) {
    return {
      shouldPlay: true,
      autoPlayCount,
    }
  }

  return {
    shouldPlay: false,
    autoPlayCount: 0,
  }
}
